import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import { useCart } from "@/contexts/CartContext";
import { formatTL } from "@/lib/api";
import { ShoppingBag } from "lucide-react";
import FavoriteButton from "@/components/FavoriteButton";

export default function ProductCard({ product, newDays = 14 }) {
  const { lang, t } = useLang();
  const { addItem } = useCart();
  const name = lang === "tr" ? product.name_tr : product.name_en;
  const hasDiscount = product.discounted_price && product.discounted_price < product.price;
  const isNewByDate = product.created_at && (Date.now() - new Date(product.created_at).getTime()) < newDays * 86_400_000;
  const showNewBadge = product.is_new || isNewByDate;

  return (
    <div data-testid={`product-card-${product.id}`} className="group relative">
      {/* Favorite heart — overlays the image. Renders outside the <Link> so it
          doesn't trigger navigation when toggled. */}
      <FavoriteButton productId={product.id} productName={name} variant="card" />
      <Link
        to={`/products/${product.seo_slug || product.id}`}
        className="block relative overflow-hidden bg-[#F5F2EC] aspect-[4/5]"
      >
        <img
          src={product.images?.[0] || "https://images.unsplash.com/photo-1605100804763-247f67b2548e?auto=format&fit=crop&w=600&q=80"}
          alt={name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
        />
        {showNewBadge && (
          <span className="absolute top-4 left-4 bg-bone text-[10px] tracking-[0.2em] uppercase px-3 py-1 font-medium">
            {lang === "tr" ? "Yeni" : "New"}
          </span>
        )}
        {hasDiscount && (
          <span className={"absolute left-4 bg-[#C8A96A] text-white text-[10px] tracking-[0.2em] uppercase px-3 py-1 font-medium " + (showNewBadge ? "top-12" : "top-4")}>
            {lang === "tr" ? "İndirim" : "Sale"}
          </span>
        )}
        {product.stock_status === "out_of_stock" && (
          <span className="absolute bottom-4 left-4 right-4 text-center bg-[#0A0A0A]/85 text-white text-[10px] tracking-[0.2em] uppercase py-2 font-medium">
            {lang === "tr" ? "Stokta Yok" : "Out of Stock"}
          </span>
        )}
        {product.stock_status === "coming_soon" && (
          <span className="absolute bottom-4 left-4 right-4 text-center bg-[#C8A96A] text-white text-[10px] tracking-[0.2em] uppercase py-2 font-medium">
            {lang === "tr" ? "Çok Yakında" : "Coming Soon"}
          </span>
        )}
        {(!product.stock_status || product.stock_status === "in_stock") && (
        <button
          data-testid={`quick-add-${product.id}`}
          onClick={(e) => { e.preventDefault(); addItem(product); }}
          className="absolute bottom-4 left-4 right-4 bg-[#0A0A0A] text-white py-3 text-xs uppercase tracking-[0.2em] opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 flex items-center justify-center gap-2"
        >
          <ShoppingBag size={14} /> {t("product.add_to_cart")}
        </button>
        )}
      </Link>
      <div className="mt-4 px-1 min-w-0">
        <h3 className="font-heading text-lg leading-tight text-[#0A0A0A] truncate">{name}</h3>
        <p className="text-[11px] uppercase tracking-[0.15em] text-[#71717A] mt-1 truncate">{product.code || product.sku}</p>
        <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 min-w-0">
          {hasDiscount ? (
            <>
              <span className="font-finance text-[#C8A96A] text-base truncate">{formatTL(product.discounted_price)}</span>
              <span className="font-finance text-xs text-[#A1A1AA] line-through truncate">{formatTL(product.price)}</span>
            </>
          ) : (
            <span className="font-finance text-[#0A0A0A] text-base truncate">{formatTL(product.price)}</span>
          )}
        </div>
      </div>
    </div>
  );
}
